/**
 * AdminStudentSalePage.jsx  —  /admin/students/:userId/sale
 *
 * Faz C: Satış Sayfası
 * Eski sistem satış ekranı birebir referans.
 * 3 panel: Eğitim Seçimi | Satış Bilgileri | Ödeme Planı | Onaylama
 *
 * Endpoint: POST /admin/students/{id}/enroll-courses (Faz C alanları)
 *   installment_plan, first_due_date, discount_amount, discount_percent,
 *   satis_personel_id, onay_kodu, referans_kodu, ek_ders_sonra_secilecek
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { ConfirmDialog } from '../components/admin';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { danismanOptionsList } from '../hooks/useDanismanlarOptions';
import { fmtTRY } from '../utils/formatCurrency';
import { useAccountingBanks } from '../api/queries/accounting';
// FAZ 1 satış sadeleştirme (2026-09-25) — satış anında POS hesabı tahsilat
// hesabı olarak KABUL EDİLMEZ, yalnızca kasa/banka listelenir.
import { isPosAccountType } from '../lib/posNetAmount';
// FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan, bkz.
// components/sales/PaymentPlanFields.jsx dosya başı yorumu.
import PaymentPlanFields from '../components/sales/PaymentPlanFields';
import {
  User,
  BookOpen,
  Award,
  CreditCard,
  Search,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MessageSquare,
  Tag,
  ShoppingCart,
  ExternalLink,
  ReceiptText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none transition-colors';

const LBL_CLS = 'text-[11px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide';

const SECTION_CLS = 'bg-white rounded-2xl border-2 border-[var(--border-light)] p-5 space-y-4';

const PANEL_TITLE_CLS = 'flex items-center gap-2 font-display font-bold text-base text-[var(--text-main)] mb-4';

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionTitle({ icon, children }) {
  return (
    <div className={PANEL_TITLE_CLS}>
      {icon}
      {children}
    </div>
  );
}

function Field({ label, required, error, hint, children }) {
  return (
    <div>
      <label className={LBL_CLS}>
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[10px] text-[var(--text-muted)] mt-1">{hint}</p>}
      {error && <p className="text-[10px] text-rose-600 mt-1 font-bold flex items-center gap-1"><AlertCircle size={11} />{error}</p>}
    </div>
  );
}

// ── Panel 1: Eğitim Seçimi ────────────────────────────────────────────────────

function CourseSelectionPanel({ courses, certTypes, cartItems, onAdd, onRemove, lang, sinavHarciDahil, setSinavHarciDahil }) {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return courses.filter((c) =>
      !q ||
      (c.title_tr || c.title || '').toLowerCase().includes(q) ||
      (c.category || '').toLowerCase().includes(q)
    );
  }, [courses, search]);

  const getTitle = (c) => (lang === 'tr' ? c.title_tr || c.title : c.title || c.title_tr) || c.course_id;

  // Düzeltme 2026-09-28 — kutu işaretli değilken (fiyat sertifikayı
  // içermiyor) eklenecek toplam sertifika ücretini gösteren ipucu için.
  const selectedCertFeeTotal = useMemo(
    () =>
      cartItems.reduce((sum, item) => {
        if (!item.certificate_type_id) return sum;
        const ct = certTypes.find((c) => c.cert_id === item.certificate_type_id);
        return sum + Number(ct?.exam_fee || 0);
      }, 0),
    [cartItems, certTypes]
  );

  return (
    <div className={SECTION_CLS}>
      <SectionTitle icon={<BookOpen size={18} className="text-[var(--brand-primary)]" />}>
        {lang === 'tr' ? 'Eğitim Seçimi' : 'Course Selection'}
      </SectionTitle>

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === 'tr' ? 'Kurs ara...' : 'Search courses...'}
          className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
        />
      </div>

      {/* Compact course table — tek satır per kurs */}
      <div className="border-2 border-[var(--border-light)] rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <p className="p-4 text-center text-sm text-gray-400">
            {lang === 'tr' ? 'Kurs bulunamadı' : 'No courses found'}
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b-2 border-[var(--border-light)] sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase">
                    {lang === 'tr' ? 'Kurs' : 'Course'}
                  </th>
                  <th className="text-right px-2 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase w-24">
                    {lang === 'tr' ? 'Fiyat' : 'Price'}
                  </th>
                  <th className="text-center px-2 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase w-20">
                    {lang === 'tr' ? '+ Ana' : '+ Main'}
                  </th>
                  <th className="text-center px-2 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase w-20">
                    {lang === 'tr' ? '+ Ek' : '+ Extra'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, idx) => {
                  const isMeb = !!c.meb_onayli;
                  const alreadyMain = cartItems.some((ci) => ci.course_id === c.course_id && ci.is_primary);
                  const alreadyExtra = cartItems.some((ci) => ci.course_id === c.course_id && !ci.is_primary);
                  const isEven = idx % 2 === 0;

                  return (
                    <tr key={c.course_id} className={`border-t border-[var(--border-light)] ${isEven ? 'bg-white' : 'bg-gray-50/60'} hover:bg-cyan-50/40 transition-colors`}>
                      <td className="px-3 py-1.5">
                        <p className="font-bold text-xs truncate max-w-[200px]">{getTitle(c)}</p>
                        <p className="text-[10px] text-[var(--text-muted)]">{c.category}</p>
                      </td>
                      <td className="px-2 py-1.5 text-right font-bold text-xs text-gray-700">
                        {c.price ? fmtTRY(c.price) : (lang === 'tr' ? 'Ücretsiz' : 'Free')}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {alreadyMain ? (
                          <CheckCircle2 size={14} className="text-emerald-500 mx-auto" />
                        ) : (
                          <motion.button
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.85 }}
                            onClick={() => onAdd(c, true)}
                            className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg bg-cyan-100 hover:bg-cyan-200 text-cyan-700 text-[11px] font-bold transition-colors"
                            title={lang === 'tr' ? 'Ana Eğitim Ekle' : 'Add as Main'}
                          >
                            <Plus size={11} />{lang === 'tr' ? 'Ana' : 'Main'}
                          </motion.button>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {isMeb ? (
                          <span
                            className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg bg-gray-100 text-gray-300 text-[11px] font-bold cursor-not-allowed"
                            title={lang === 'tr' ? 'MEB onaylı — yalnızca ana eğitim' : 'MEB-approved — main only'}
                          >
                            <Plus size={11} />{lang === 'tr' ? 'Ek' : 'Extra'}
                          </span>
                        ) : alreadyExtra ? (
                          <CheckCircle2 size={14} className="text-emerald-500 mx-auto" />
                        ) : (
                          <motion.button
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.85 }}
                            onClick={() => onAdd(c, false)}
                            className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[11px] font-bold transition-colors"
                            title={lang === 'tr' ? 'Ek Eğitim Ekle' : 'Add as Extra'}
                          >
                            <Plus size={11} />{lang === 'tr' ? 'Ek' : 'Extra'}
                          </motion.button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cart */}
      <div>
        <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-2">
          <ShoppingCart size={12} className="inline mr-1" />
          {lang === 'tr' ? 'Seçilen Eğitimler' : 'Selected Courses'} ({cartItems.length})
        </p>
        {cartItems.length === 0 ? (
          <div className="border-2 border-dashed border-[var(--border-light)] rounded-xl p-4 text-center text-sm text-gray-400">
            {lang === 'tr' ? 'Henüz eğitim seçilmedi' : 'No courses selected yet'}
          </div>
        ) : (
          <div className="space-y-1.5">
            {cartItems.map((item, i) => (
              <div
                key={`${item.course_id}-${item.is_primary}`}
                className="flex items-center gap-2.5 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white"
              >
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${item.is_primary ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {item.is_primary ? (lang === 'tr' ? 'ANA' : 'MAIN') : (lang === 'tr' ? 'EK' : 'EXTRA')}
                </span>
                <span className="flex-1 text-xs font-bold truncate">{item.title}</span>
                <span className="text-xs font-bold text-gray-600">{fmtTRY(item.price || 0)}</span>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onRemove(i)}
                  className="p-1 rounded-lg hover:bg-rose-100 text-gray-400 hover:text-rose-600 transition-colors"
                >
                  <Trash2 size={13} />
                </motion.button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cert / sınav — per-cart item cert selection */}
      {cartItems.length > 0 && (
        <div>
          <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-2">
            <Award size={12} className="inline mr-1" />
            {lang === 'tr' ? 'Sertifika / Sınav Giriş' : 'Certificate / Exam Entry'}
          </p>
          <div className="space-y-2">
            {cartItems.map((item, i) => (
              <div key={`cert-${i}`} className="flex gap-3 items-start p-3 rounded-xl border-2 border-[var(--border-light)] bg-gray-50">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate mb-1.5">{item.title}</p>
                  <Field label={lang === 'tr' ? 'Sertifika Tipi' : 'Certificate Type'}>
                    <select
                      value={item.certificate_type_id || ''}
                      onChange={(e) => {
                        item._onCertChange && item._onCertChange(i, 'certificate_type_id', e.target.value || null);
                      }}
                      className={INPUT_CLS}
                    >
                      <option value="">{lang === 'tr' ? 'Seçiniz...' : 'Select...'}</option>
                      {certTypes.map((ct) => (
                        <option key={ct.cert_id} value={ct.cert_id}>
                          {lang === 'tr' ? ct.name : (ct.name_en || ct.name)} — {fmtTRY(ct.exam_fee || 0)}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                {item.certificate_type_id && (
                  <div className="min-w-[120px]">
                    <Field label={lang === 'tr' ? 'Sınav Tarihi' : 'Exam Date'}>
                      <input
                        type="date"
                        value={item.exam_entry_date || ''}
                        onChange={(e) =>
                          item._onCertChange && item._onCertChange(i, 'exam_entry_date', e.target.value)
                        }
                        className={INPUT_CLS}
                      />
                    </Field>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Görev 4, düzeltme 2026-09-28: kutu artık DersEkleKart'taki
              `certificate_fee_included` ile AYNI i18n metnini ve semantiğini
              kullanır — işaretli = fiyat sertifikayı zaten içeriyor, ücret
              toplama eklenmez; işaretsiz = ücret eklenir. */}
          <label className="flex items-center gap-2 cursor-pointer mt-3 text-sm font-semibold">
            <input
              type="checkbox"
              checked={sinavHarciDahil}
              onChange={(e) => setSinavHarciDahil(e.target.checked)}
              className="w-4 h-4 rounded accent-[var(--brand-primary)]"
              data-testid="admin-sale-certificate-fee-included"
            />
            {t('sales.certificate_fee_included_label')}
          </label>
          {!sinavHarciDahil && selectedCertFeeTotal > 0 && (
            <p className="text-xs mt-1 text-[var(--text-muted)]">
              {lang === 'tr' ? `+${fmtTRY(selectedCertFeeTotal)} eklenecek` : `+${fmtTRY(selectedCertFeeTotal)} will be added`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Panel 2: Satış Bilgileri ──────────────────────────────────────────────────

function SalesInfoPanel({
  lang,
  referansKodu, setReferansKodu,
  referansResult, setReferansResult,
  referansLoading, setReferansLoading,
  discountType, setDiscountType,
  discountValue, setDiscountValue,
  authHeaders,
  totalListPrice, discountAmount, saleTotalPrice,
}) {
  const toast = useToast();

  const queryReferans = async () => {
    if (!referansKodu.trim()) return;
    setReferansLoading(true);
    setReferansResult(null);
    try {
      const r = await axios.get(
        `${API}/admin/referans-kodlari/${encodeURIComponent(referansKodu.trim())}/sorgula`,
        { headers: authHeaders() }
      );
      const data = r.data;
      setReferansResult(data);
      if (data.gecerli) {
        // auto-apply indirim
        if (data.indirim_tipi === 'yuzde') {
          setDiscountType('percent');
          setDiscountValue(data.indirim_degeri);
        } else if (data.indirim_tipi === 'tutar') {
          setDiscountType('amount');
          setDiscountValue(data.indirim_degeri);
        }
        toast.success(lang === 'tr' ? 'Referans kodu geçerli — indirim uygulandı' : 'Referral code valid — discount applied');
      }
    } catch (err) {
      setReferansResult({ gecerli: false, sebep: getApiErrorMessage(err, lang) });
    } finally {
      setReferansLoading(false);
    }
  };

  return (
    <div className={SECTION_CLS}>
      <SectionTitle icon={<Tag size={18} className="text-amber-500" />}>
        {lang === 'tr' ? 'Satış Bilgileri' : 'Sales Information'}
      </SectionTitle>

      {/* Toplam liste tutarı */}
      <div className="flex items-center justify-between px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50">
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide">
          {lang === 'tr' ? 'Toplam Liste Tutarı' : 'Total List Price'}
        </span>
        <span className="font-display font-bold text-base text-[var(--text-main)]">{fmtTRY(totalListPrice)}</span>
      </div>

      {/* Referans kodu */}
      <Field label={lang === 'tr' ? 'Referans Kodu (Opsiyonel)' : 'Referral Code (Optional)'}>
        <div className="flex gap-2">
          <input
            type="text"
            value={referansKodu}
            onChange={(e) => { setReferansKodu(e.target.value.toUpperCase()); setReferansResult(null); }}
            placeholder={lang === 'tr' ? 'REF001' : 'REF001'}
            className={INPUT_CLS + ' flex-1'}
          />
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={queryReferans}
            disabled={!referansKodu.trim() || referansLoading}
            className="px-3 py-2 rounded-xl border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] text-xs font-bold hover:bg-cyan-50 disabled:opacity-40 whitespace-nowrap"
          >
            {referansLoading ? <Loader2 size={13} className="animate-spin" /> : (lang === 'tr' ? 'Sorgula' : 'Query')}
          </motion.button>
        </div>
        {referansResult && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-xs font-bold ${
              referansResult.gecerli
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-rose-50 border-rose-200 text-rose-700'
            }`}
          >
            {referansResult.gecerli ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
            {referansResult.gecerli
              ? (referansResult.aciklama || (lang === 'tr' ? 'Geçerli kod' : 'Valid code'))
              : (referansResult.sebep || (lang === 'tr' ? 'Geçersiz kod' : 'Invalid code'))}
          </motion.div>
        )}
      </Field>

      {/* İndirim */}
      <div className="space-y-2">
        <label className={LBL_CLS}>{lang === 'tr' ? 'İndirim' : 'Discount'}</label>
        <div className="flex gap-2">
          <div className="flex rounded-xl border-2 border-[var(--border-light)] overflow-hidden">
            {['percent', 'amount'].map((tp) => (
              <button
                key={tp}
                type="button"
                onClick={() => setDiscountType(tp)}
                className={`px-3 py-2 text-xs font-bold transition-colors ${
                  discountType === tp
                    ? 'bg-[var(--brand-primary)] text-white'
                    : 'bg-white text-[var(--text-muted)] hover:bg-gray-50'
                }`}
              >
                {tp === 'percent' ? '%' : '₺'}
              </button>
            ))}
          </div>
          <input
            type="number"
            min={0}
            max={discountType === 'percent' ? 100 : undefined}
            step={discountType === 'percent' ? 0.1 : 1}
            value={discountValue}
            onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
            className={INPUT_CLS + ' flex-1'}
          />
        </div>
      </div>

      {/* Özet hesap */}
      <div className="border-2 border-[var(--border-light)] rounded-xl p-3 space-y-1.5 bg-gray-50">
        <div className="flex justify-between text-sm">
          <span className="text-[var(--text-muted)]">{lang === 'tr' ? 'Liste Tutarı' : 'List Price'}:</span>
          <span className="font-bold">{fmtTRY(totalListPrice)}</span>
        </div>
        {discountAmount > 0 && (
          <div className="flex justify-between text-sm text-emerald-600">
            <span>{lang === 'tr' ? 'İndirim' : 'Discount'} ({discountType === 'percent' ? `%${discountValue}` : fmtTRY(discountValue)}):</span>
            <span>−{fmtTRY(discountAmount)}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold border-t border-gray-200 pt-1.5">
          <span>{lang === 'tr' ? 'Toplam Satış Tutarı' : 'Sale Total'}:</span>
          <span className="text-[var(--brand-primary)]">{fmtTRY(saleTotalPrice)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Panel 3: Ödeme Planı ─────────────────────────────────────────────────────

function PaymentPlanPanel({
  lang,
  saleTotalPrice,
  collectionBanks, banksLoading,
  disabled,
  resetKey,
  onPaymentPlanChange,
}) {
  return (
    <div className={SECTION_CLS}>
      <SectionTitle icon={<CreditCard size={18} className="text-[var(--brand-primary)]" />}>
        {lang === 'tr' ? 'Ödeme Planı' : 'Payment Plan'}
      </SectionTitle>

      {/* FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan, bkz.
          components/sales/PaymentPlanFields.jsx dosya başı yorumu. */}
      <PaymentPlanFields
        key={resetKey}
        role="admin"
        totalAmount={saleTotalPrice}
        banks={collectionBanks}
        banksLoading={banksLoading}
        disabled={disabled}
        onChange={onPaymentPlanChange}
      />
    </div>
  );
}

// ── Panel 4: Onaylama ─────────────────────────────────────────────────────────

function ApprovalPanel({
  lang,
  ekDersSonraSecilecek, setEkDersSonraSecilecek,
  ekDersSonraSecilecekAdet, setEkDersSonraSecilecekAdet,
  kayitAciklama, setKayitAciklama,
  satisPersonelId, setSatisPersonelId,
  onayKodu, setOnayKodu,
  personelList,
  userId,
  authHeaders,
  canSubmit,
  submitting,
  onSubmit,
  diffIsZero,
  hasMinOnePrimary,
}) {
  const toast = useToast();
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);

  const sendApprovalCode = async () => {
    if (smsSending || smsSent) return;
    setSmsSending(true);
    try {
      await axios.post(
        `${API}/admin/students/${userId}/send-approval-code`,
        {},
        { headers: authHeaders() }
      );
      setSmsSent(true);
      toast.success(lang === 'tr' ? 'Onay kodu SMS ile gönderildi' : 'Approval code sent via SMS');
      setTimeout(() => setSmsSent(false), 8000);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSmsSending(false);
    }
  };

  const blockReasons = [];
  if (!hasMinOnePrimary) {
    blockReasons.push(lang === 'tr' ? 'En az bir Ana Eğitim seçilmeli' : 'At least one Main course required');
  }
  if (!satisPersonelId) {
    blockReasons.push(lang === 'tr' ? 'Satış personeli seçilmeli' : 'Sales staff must be selected');
  }
  if (!diffIsZero) {
    blockReasons.push(lang === 'tr' ? 'Ödeme planı farkı sıfır olmalı' : 'Payment plan difference must be zero');
  }

  return (
    <div className={SECTION_CLS}>
      <SectionTitle icon={<CheckCircle2 size={18} className="text-emerald-500" />}>
        {lang === 'tr' ? 'Onaylama' : 'Confirmation'}
      </SectionTitle>

      {/* Ek ders */}
      <div className="flex items-start gap-3">
        <label className="flex items-center gap-2 cursor-pointer mt-0.5">
          <input
            type="checkbox"
            checked={ekDersSonraSecilecek}
            onChange={(e) => {
              setEkDersSonraSecilecek(e.target.checked);
              if (!e.target.checked) setEkDersSonraSecilecekAdet(0);
            }}
            className="w-4 h-4 rounded accent-[var(--brand-primary)]"
          />
        </label>
        <div className="flex-1">
          <p className="text-sm font-semibold">
            {lang === 'tr' ? 'Ek Ders Sonra Seçilecek' : 'Extra Lessons To Be Selected Later'}
          </p>
          {ekDersSonraSecilecek && (
            <div className="mt-2">
              <Field label={lang === 'tr' ? 'Adet' : 'Count'}>
                <input
                  type="number"
                  min={1}
                  value={ekDersSonraSecilecekAdet}
                  onChange={(e) => setEkDersSonraSecilecekAdet(parseInt(e.target.value) || 0)}
                  className={INPUT_CLS + ' max-w-[120px]'}
                />
              </Field>
            </div>
          )}
        </div>
      </div>

      {/* Kayıt açıklama */}
      <Field label={lang === 'tr' ? 'Kayıt Açıklaması' : 'Registration Notes'}>
        <textarea
          rows={3}
          value={kayitAciklama}
          onChange={(e) => setKayitAciklama(e.target.value)}
          className={INPUT_CLS + ' resize-none'}
          placeholder={lang === 'tr' ? 'İsteğe bağlı not...' : 'Optional note...'}
        />
      </Field>

      {/* Satış personeli */}
      <Field
        label={lang === 'tr' ? 'Satış Yapan Personel' : 'Sales Staff'}
        required
        error={!satisPersonelId ? (lang === 'tr' ? 'Bu alan zorunludur' : 'Required') : undefined}
      >
        <select
          value={satisPersonelId}
          onChange={(e) => setSatisPersonelId(e.target.value)}
          className={INPUT_CLS}
        >
          <option value="">{lang === 'tr' ? 'Personel seçiniz...' : 'Select staff...'}</option>
          {danismanOptionsList(personelList, (t, e) => (lang === 'tr' ? t : e), { disablePasif: true }).map((o) => (
            <option key={o.key} value={o.value} disabled={o.disabled}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* Onay kodu */}
      <Field label={lang === 'tr' ? 'Onay Kodu' : 'Approval Code'}>
        <div className="flex gap-2">
          <input
            type="text"
            value={onayKodu}
            onChange={(e) => setOnayKodu(e.target.value)}
            placeholder={lang === 'tr' ? 'Öğrenciden alınan kod...' : 'Code received from student...'}
            className={INPUT_CLS + ' flex-1 font-mono tracking-wider'}
          />
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={sendApprovalCode}
            disabled={smsSending || smsSent}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] text-xs font-bold hover:bg-cyan-50 disabled:opacity-50 whitespace-nowrap"
          >
            {smsSending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : smsSent ? (
              <CheckCircle2 size={13} />
            ) : (
              <MessageSquare size={13} />
            )}
            {smsSent
              ? (lang === 'tr' ? 'Gönderildi ✓' : 'Sent ✓')
              : (lang === 'tr' ? 'SMS Gönder' : 'Send SMS')}
          </motion.button>
        </div>
      </Field>

      {/* Block reasons */}
      {blockReasons.length > 0 && (
        <div className="space-y-1">
          {blockReasons.map((r, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border-2 border-rose-200 text-rose-700 text-xs font-bold">
              <AlertCircle size={12} />
              {r}
            </div>
          ))}
        </div>
      )}

      {/* Submit */}
      <motion.button
        whileHover={canSubmit && !submitting ? { scale: 1.02 } : {}}
        whileTap={canSubmit && !submitting ? { scale: 0.98 } : {}}
        onClick={onSubmit}
        disabled={!canSubmit || submitting}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white text-sm font-bold border-b-4 border-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        data-testid="sale-confirm-btn"
      >
        {submitting ? (
          <><Loader2 size={16} className="animate-spin" /> {lang === 'tr' ? 'Kaydediliyor...' : 'Saving...'}</>
        ) : (
          <><CheckCircle2 size={16} /> {lang === 'tr' ? 'SATIŞI ONAYLA' : 'CONFIRM SALE'}</>
        )}
      </motion.button>
    </div>
  );
}

// ── Success Card ──────────────────────────────────────────────────────────────

function SuccessCard({ result, userId, lang, navigate, sinavHarciDahil }) {
  // FAZ 1 düzeltme turu — madde B: başarı ekranı backend yanıtındaki GERÇEK
  // tutarları gösterir (`result.total_amount`, `result.enrolled_courses[].
  // certificate_fee`) — sayfanın kendi yerel state'inden (saleTotalPrice,
  // certFeesSubtotal) HİÇBİR ŞEY okunmaz; satış zaten tamamlandı, tek doğru
  // kaynak artık sunucunun yazdığı kayıttır.
  const certFeeTotal = (result?.enrolled_courses || []).reduce(
    (s, c) => s + (Number(c.certificate_fee) || 0),
    0
  );
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="max-w-xl mx-auto space-y-5"
    >
      <div className="text-center py-6">
        <div
          className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3"
          style={{ boxShadow: '0 4px 0 0 #059669' }}
        >
          <CheckCircle2 size={32} className="text-white" strokeWidth={2.5} />
        </div>
        <h2 className="font-display text-2xl font-bold text-[var(--text-main)] mb-1">
          {lang === 'tr' ? 'Satış Kaydedildi!' : 'Sale Recorded!'}
        </h2>
        <p className="text-sm text-[var(--text-muted)]">
          {lang === 'tr' ? 'Kayıt başarıyla tamamlandı.' : 'Enrollment completed successfully.'}
        </p>
      </div>

      {/* Result summary */}
      {result && (
        <div className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-5 space-y-3">
          {result.fatura_no && (
            <div className="flex items-center gap-2">
              <ReceiptText size={14} className="text-[var(--brand-primary)]" />
              <span className="text-sm font-bold">
                {lang === 'tr' ? 'Kayıt No:' : 'Record No:'} <code className="font-mono">{result.fatura_no}</code>
              </span>
            </div>
          )}
          {typeof result.total_amount === 'number' && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)] font-semibold">
                {lang === 'tr' ? 'Toplam Tutar' : 'Total Amount'}
              </span>
              <span className="font-bold text-[var(--text-main)]">{fmtTRY(result.total_amount)}</span>
            </div>
          )}
          {certFeeTotal > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs font-bold">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={13} />
                {lang === 'tr' ? 'Sertifika Ücreti (borca dahil)' : 'Certificate Fee (included)'}
              </span>
              <span>{fmtTRY(certFeeTotal)}</span>
            </div>
          )}
          {sinavHarciDahil && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold">
              <CheckCircle2 size={13} />
              {lang === 'tr' ? 'Sınav harcı dahil' : 'Exam fee included'}
            </div>
          )}
          {result.installment_plan && result.installment_plan.length > 0 && (
            <div>
              <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-2">
                {lang === 'tr' ? 'Taksit Planı' : 'Payment Plan'}
              </p>
              <div className="space-y-1">
                {result.installment_plan.map((row, i) => (
                  <div key={i} className="flex justify-between text-xs px-2 py-1 rounded border border-[var(--border-light)]">
                    <span className="text-[var(--text-muted)]">{row.due_date}</span>
                    <span className="font-bold">{fmtTRY(row.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate(`/admin/students/${userId}`)}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold hover:bg-gray-50"
        >
          <ExternalLink size={15} />
          {lang === 'tr' ? 'Öğrenci Profiline Git' : 'Go to Student Profile'}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/admin/students')}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)]"
        >
          {lang === 'tr' ? 'Öğrenci Listesine Dön' : 'Back to Students'}
        </motion.button>
      </div>
    </motion.div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminStudentSalePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  // Bu sayfa yalnızca /admin/students/:userId/sale (admin/superadmin,
  // RoleRoute) altında render edilir — GET /admin/banks require_admin, her
  // zaman açık.
  const { data: allBanks = [], isLoading: banksLoading } = useAccountingBanks();
  const collectionBanks = allBanks.filter((b) => !isPosAccountType(b.account_type));

  // ── Student info ──────────────────────────────────────────────────────────
  const [student, setStudent] = useState(null);
  const [studentLoading, setStudentLoading] = useState(true);

  // ── Master data ───────────────────────────────────────────────────────────
  const [courses, setCourses] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const [personelList, setPersonelList] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // ── Cart ──────────────────────────────────────────────────────────────────
  const [cartItems, setCartItems] = useState([]);

  // ── Sales info ────────────────────────────────────────────────────────────
  const [referansKodu, setReferansKodu] = useState('');
  const [referansResult, setReferansResult] = useState(null);
  const [referansLoading, setReferansLoading] = useState(false);
  const [discountType, setDiscountType] = useState('percent'); // 'percent' | 'amount'
  const [discountValue, setDiscountValue] = useState(0);

  // ── Payment plan ──────────────────────────────────────────────────────────
  // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları/doğrulaması
  // artık paylaşılan components/sales/PaymentPlanFields.jsx tarafından
  // yönetilir (bkz. o dosyanın başı yorumu).
  const [paymentPlanFields, setPaymentPlanFields] = useState({});
  const [paymentPlanValid, setPaymentPlanValid] = useState(false);
  // Bu sayfa tek seferlik satış akışıdır — başarıda successResult set edilip
  // form komple unmount olur (aşağıda), bu yüzden PaymentPlanFields'ı
  // sıfırlayıp aynı formu yeniden kullanma ihtiyacı yok (DersEkleKart /
  // StudentEnrollmentsTab'daki resetKey deseninin aksine setter'a gerek yok).
  const [paymentPlanResetKey] = useState(0);

  // ── Approval ──────────────────────────────────────────────────────────────
  const [ekDersSonraSecilecek, setEkDersSonraSecilecek] = useState(false);
  const [ekDersSonraSecilecekAdet, setEkDersSonraSecilecekAdet] = useState(0);
  const [kayitAciklama, setKayitAciklama] = useState('');
  const [satisPersonelId, setSatisPersonelId] = useState('');
  const [onayKodu, setOnayKodu] = useState('');

  // ── Sınav harcı ──────────────────────────────────────────────────────────
  // Düzeltme 2026-09-28 — DersEkleKart'taki `certificate_fee_included` ile
  // AYNI semantik ve varsayılan: işaretli = fiyat sertifikayı zaten içerir,
  // ücret toplama eklenmez. Varsayılan işaretli (tutarlılık, Recep kararı).
  // DİKKAT: bu, önceki "ücret her zaman eklenir" davranışından (madde B,
  // inv_bad9301c2deb kök neden fix'i) farklıdır — artık admin sertifika
  // ücretinin faturaya girmesi için bilerek kutuyu İŞARETSİZ bırakmalı.
  const [sinavHarciDahil, setSinavHarciDahil] = useState(true);

  // ── Submit ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [successResult, setSuccessResult] = useState(null);

  // ── Confirm dialog ────────────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Load student ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    axios
      .get(`${API}/admin/students/${userId}/detail`, { headers: authHeaders() })
      .then((r) => setStudent(r.data?.student || r.data?.user || r.data))
      .catch(() => toast.error(lang === 'tr' ? 'Öğrenci yüklenemedi' : 'Failed to load student'))
      .finally(() => setStudentLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ── Load master data ──────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/courses?limit=200`).then((r) => r.data.courses || []),
      axios.get(`${API}/admin/certificates/types`, { headers: authHeaders() }).then((r) => r.data.certificates || []),
      // Satış personeli seçimi — atama amaçlı: HER ZAMAN aktif=tumu, pasif
      // danışmanlar disablePasif ile render sırasında seçilemez yapılır
      // (bkz. hooks/useDanismanlarOptions.js dosya başı yorumu).
      axios
        .get(`${API}/admin/danismanlar?include_admins=true&aktif=tumu`, { headers: authHeaders() })
        .then((r) => r.data.danismanlar || r.data.users || r.data || [])
        .catch(() => []),
    ])
      .then(([c, ct, p]) => {
        setCourses(c);
        setCertTypes(ct);
        setPersonelList(p);
      })
      .catch(() => toast.error(lang === 'tr' ? 'Veri yüklenemedi' : 'Failed to load data'))
      .finally(() => setDataLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cart helpers ──────────────────────────────────────────────────────────

  const handleAddToCart = useCallback(
    (course, isPrimary) => {
      // MEB guard
      if (!isPrimary && course.meb_onayli) {
        toast.error(
          lang === 'tr'
            ? 'MEB onaylı kurs yalnızca ana eğitim olarak eklenebilir'
            : 'MEB-approved course can only be added as main education'
        );
        return;
      }
      // Already in cart guard
      const exists = cartItems.some((c) => c.course_id === course.course_id && c.is_primary === isPrimary);
      if (exists) {
        toast.error(lang === 'tr' ? 'Bu kurs zaten sepette' : 'This course is already in the cart');
        return;
      }
      setCartItems((prev) => [
        ...prev,
        {
          course_id: course.course_id,
          title: (lang === 'tr' ? course.title_tr || course.title : course.title || course.title_tr) || course.course_id,
          price: course.price || 0,
          is_primary: isPrimary,
          certificate_type_id: null,
          exam_entry_date: '',
          _onCertChange: null, // will be attached below
        },
      ]);
    },
    [cartItems, lang, toast]
  );

  // Attach cert change handler via mutation
  const handleCertChange = useCallback((index, field, value) => {
    setCartItems((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], [field]: value };
      return arr;
    });
  }, []);

  // Attach _onCertChange to each cart item (so CourseSelectionPanel can call it)
  const cartWithHandlers = useMemo(
    () => cartItems.map((item) => ({ ...item, _onCertChange: handleCertChange })),
    [cartItems, handleCertChange]
  );

  const handleRemoveFromCart = useCallback((index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Computed values ───────────────────────────────────────────────────────

  // FAZ 1 düzeltme turu — madde B (2026-09-25, kök neden kanıtı: invoice
  // inv_bad9301c2deb — MEB sertifikalı satışta 500 TL'lik sınav ücreti hiç
  // faturaya girmemişti). Seçilen sertifika tipinin ücreti (`certTypes[].
  // exam_fee`) borca dahil edilebilir. Backend TEK KAYNAK
  // (`routes/admin.py::admin_enroll_student_courses`) indirim yüzdesini
  // YALNIZCA kurs fiyatına uygular, sertifika ücretine DEĞİL:
  // `price *= (1 - discount_percent/100)` sonra `billable = price +
  // cert_fee_to_add` — aşağıdaki hesap bilerek AYNI sırayı izler (tutar
  // bazlı indirim davranışı bu turun kapsamı dışı, önceki haliyle AYNI kaldı).
  // Düzeltme 2026-09-28: `certFeeFor` HAM ücreti döndürür (checkbox'tan
  // bağımsız) — "eklenip eklenmeyeceği" kararı `sinavHarciDahil` kutusunda
  // (aşağıdaki `certFeesSubtotal`/payload'da) verilir; DersEkleKart'taki
  // (`certificate_fee_included`) ile AYNI semantik: kutu işaretli = fiyat
  // sertifikayı zaten içerir, eklenmez.
  const certFeeFor = useCallback(
    (item) => {
      if (!item.certificate_type_id) return 0;
      const ct = certTypes.find((c) => c.cert_id === item.certificate_type_id);
      return Number(ct?.exam_fee || 0);
    },
    [certTypes]
  );

  // Backend ana + ek kursların tümünü tam fiyatından faturalar (bkz.
  // admin_enroll_student_courses) — arayüzdeki toplam da aynı şekilde TÜM
  // sepet kalemlerini kapsamalı, yalnızca ana kalemleri değil.
  const coursesSubtotal = useMemo(
    () => cartItems.reduce((s, c) => s + (c.price || 0), 0),
    [cartItems]
  );

  // Düzeltme 2026-09-28 — `sinavHarciDahil` işaretliyse (fiyat sertifikayı
  // zaten içeriyor) ücret toplama EKLENMEZ. Önceden bu kutunun durumu hiç
  // okunmuyordu, ücret her zaman ekleniyordu (bkz. görev notu "B düzeltmesi
  // sertifika ücretini her zaman ekliyor").
  const certFeesSubtotal = useMemo(
    () => (sinavHarciDahil ? 0 : cartItems.reduce((s, c) => s + certFeeFor(c), 0)),
    [cartItems, certFeeFor, sinavHarciDahil]
  );

  const totalListPrice = useMemo(
    () => coursesSubtotal + certFeesSubtotal,
    [coursesSubtotal, certFeesSubtotal]
  );

  const discountAmount = useMemo(() => {
    if (discountType === 'percent') {
      // Sertifika ücreti indirime dahil DEĞİL — yalnızca kurs fiyatı tabanı.
      return Math.min(coursesSubtotal, (coursesSubtotal * discountValue) / 100);
    }
    return Math.min(totalListPrice, discountValue);
  }, [coursesSubtotal, totalListPrice, discountType, discountValue]);

  const saleTotalPrice = useMemo(
    () => Math.max(0, totalListPrice - discountAmount),
    [totalListPrice, discountAmount]
  );

  const hasMinOnePrimary = cartItems.some((c) => c.is_primary);

  const canSubmit = hasMinOnePrimary && !!satisPersonelId && paymentPlanValid;

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmitConfirmed = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      // FAZ 1 düzeltme turu — madde B, düzeltme 2026-09-28: seçilen sertifika
      // tipinin ücretini (`certTypes[].exam_fee`) yalnızca `sinavHarciDahil`
      // İŞARETSİZKEN borca dahil et (kutu işaretli = fiyat sertifikayı zaten
      // içeriyor, ücret AYRICA eklenmez — DersEkleKart'taki
      // `certificate_fee_included` ile AYNI semantik). Backend
      // (`routes/admin.py::admin_enroll_student_courses`) bu iki alanı HER
      // ROL için (admin dahil) okur; `certificate_fee_included` YOKSA/false
      // ise ücret hiç faturaya eklenmez — bu yüzden yalnızca eklenmesi
      // gerektiğinde `true` AÇIKÇA gönderilir.
      // "Sınav harcı dahil" (`sinav_harci_dahil`, aşağıda body'ye ayrıca
      // eklenir) BUNUNLA AYNI ALAN DEĞİL — yalnızca izleme amaçlı, tutara
      // etkisi yok (bkz. backend/BACKEND_GAPS.md "FAZ 1 düzeltme turu" B maddesi).
      const coursesPayload = cartItems.map((item) => {
        const certFee = certFeeFor(item);
        const addCertFee = !sinavHarciDahil && certFee > 0;
        return {
          course_id: item.course_id,
          is_primary: item.is_primary,
          ...(item.certificate_type_id ? { certificate_type_id: item.certificate_type_id } : {}),
          ...(item.exam_entry_date ? { exam_entry_date: item.exam_entry_date } : {}),
          ...(addCertFee
            ? { certificate_fee_amount: certFee, certificate_fee_included: true }
            : {}),
        };
      });

      const body = {
        courses: coursesPayload,
        satis_personel_id: satisPersonelId,
        sinav_harci_dahil: sinavHarciDahil,
        ...(onayKodu.trim() ? { onay_kodu: onayKodu.trim() } : {}),
        ...(referansKodu.trim() ? { referans_kodu: referansKodu.trim() } : {}),
        ...(kayitAciklama.trim() ? { payment_notes: kayitAciklama.trim() } : {}),
        ...(ekDersSonraSecilecek && ekDersSonraSecilecekAdet > 0
          ? { ek_ders_sonra_secilecek: ekDersSonraSecilecekAdet }
          : {}),
        // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları
        // (down_payment, down_payment_method/bank_id, installments/
        // first_due_date VEYA installment_plan, payment_method)
        // PaymentPlanFields tarafından hesaplanır (bkz. yukarıdaki onChange).
        ...paymentPlanFields,
      };

      // Discount — send only one type (amount takes priority if explicitly typed as amount)
      if (discountAmount > 0) {
        if (discountType === 'amount') {
          body.discount_amount = discountAmount;
        } else {
          body.discount_percent = discountValue;
        }
      }

      const r = await axios.post(
        `${API}/admin/students/${userId}/enroll-courses`,
        body,
        { headers: authHeaders() }
      );

      setSuccessResult(r.data || {});
      toast.success(lang === 'tr' ? 'Satış başarıyla kaydedildi!' : 'Sale recorded successfully!');
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────

  if (successResult) {
    return (
      <AdminLayout>
        <PageHeader
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: lang === 'tr' ? 'Öğrenciler' : 'Students', href: '/admin/students' },
            { label: lang === 'tr' ? 'Satış' : 'Sale' },
          ]}
          title={lang === 'tr' ? 'Satış Tamamlandı' : 'Sale Completed'}
        />
        <SuccessCard result={successResult} userId={userId} lang={lang} navigate={navigate} sinavHarciDahil={sinavHarciDahil} />
      </AdminLayout>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (studentLoading || dataLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
        </div>
      </AdminLayout>
    );
  }

  const studentName = student
    ? [student.ad, student.soyad].filter(Boolean).join(' ') || student.name || student.email || userId
    : userId;
  const studentTc = student?.tc_no || '';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: lang === 'tr' ? 'Öğrenciler' : 'Students', href: '/admin/students' },
          { label: studentName, href: `/admin/students/${userId}` },
          { label: lang === 'tr' ? 'Satış' : 'Sale' },
        ]}
        title={
          <span>
            {lang === 'tr' ? 'Kayıt: ' : 'Enroll: '}
            <span className="text-[var(--brand-primary)]">{studentName}</span>
            {studentTc && (
              <span className="ml-2 text-sm font-normal text-[var(--text-muted)]">— {studentTc}</span>
            )}
          </span>
        }
        subtitle={lang === 'tr' ? 'Satış sayfası — eğitim seçimi, ödeme planı ve onaylama' : 'Sale page — course selection, payment plan and confirmation'}
        actions={
          <Link
            to={`/admin/students/${userId}`}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold hover:bg-gray-50"
          >
            <User size={14} />
            {lang === 'tr' ? 'Öğrenci Profili' : 'Student Profile'}
          </Link>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 pb-10">
        {/* Left column */}
        <div className="space-y-5">
          <CourseSelectionPanel
            courses={courses}
            certTypes={certTypes}
            cartItems={cartWithHandlers}
            onAdd={handleAddToCart}
            onRemove={handleRemoveFromCart}
            lang={lang}
            sinavHarciDahil={sinavHarciDahil}
            setSinavHarciDahil={setSinavHarciDahil}
          />
          <SalesInfoPanel
            lang={lang}
            cartItems={cartItems}
            referansKodu={referansKodu}
            setReferansKodu={setReferansKodu}
            referansResult={referansResult}
            setReferansResult={setReferansResult}
            referansLoading={referansLoading}
            setReferansLoading={setReferansLoading}
            discountType={discountType}
            setDiscountType={setDiscountType}
            discountValue={discountValue}
            setDiscountValue={setDiscountValue}
            authHeaders={authHeaders}
            totalListPrice={totalListPrice}
            discountAmount={discountAmount}
            saleTotalPrice={saleTotalPrice}
          />
        </div>

        {/* Right column */}
        <div className="space-y-5">
          <PaymentPlanPanel
            lang={lang}
            saleTotalPrice={saleTotalPrice}
            collectionBanks={collectionBanks}
            banksLoading={banksLoading}
            disabled={submitting}
            resetKey={paymentPlanResetKey}
            onPaymentPlanChange={(fields, meta) => { setPaymentPlanFields(fields); setPaymentPlanValid(meta.isValid); }}
          />
          <ApprovalPanel
            lang={lang}
            ekDersSonraSecilecek={ekDersSonraSecilecek}
            setEkDersSonraSecilecek={setEkDersSonraSecilecek}
            ekDersSonraSecilecekAdet={ekDersSonraSecilecekAdet}
            setEkDersSonraSecilecekAdet={setEkDersSonraSecilecekAdet}
            kayitAciklama={kayitAciklama}
            setKayitAciklama={setKayitAciklama}
            satisPersonelId={satisPersonelId}
            setSatisPersonelId={setSatisPersonelId}
            onayKodu={onayKodu}
            setOnayKodu={setOnayKodu}
            personelList={personelList}
            userId={userId}
            authHeaders={authHeaders}
            canSubmit={canSubmit}
            submitting={submitting}
            onSubmit={() => setConfirmOpen(true)}
            diffIsZero={paymentPlanValid}
            hasMinOnePrimary={hasMinOnePrimary}
          />
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(v) => !submitting && setConfirmOpen(v)}
        title={lang === 'tr' ? 'Satışı Onayla' : 'Confirm Sale'}
        description={
          lang === 'tr'
            ? `${studentName} adlı öğrenci için ${fmtTRY(saleTotalPrice)} tutarında kayıt oluşturulacak. Bu işlem geri alınamaz.`
            : `An enrollment of ${fmtTRY(saleTotalPrice)} will be created for ${studentName}. This cannot be undone.`
        }
        variant="info"
        confirmLabel={lang === 'tr' ? 'Onayla ve Kaydet' : 'Confirm & Save'}
        onConfirm={handleSubmitConfirmed}
        loading={submitting}
      />
    </AdminLayout>
  );
}
